import {useEffect, useState} from "react";
import {FieldErrors, useForm} from "react-hook-form";
import {zodResolver} from "@hookform/resolvers/zod";
import {v4 as UUID} from "uuid";
import {Form} from "@/components/ui/form.tsx";
import TextInputField from "@/components/forms/TextInputField.tsx";
import HiddenField from "@/components/forms/HiddenField.tsx";
import FormToolbar from "@/components/management/FormToolbar.tsx";
import {SaveState} from "@/components/buttons/SaveButton.tsx";
import {Scene} from "@/scenes/Scene.ts";
import {SceneFormInput, SceneFormOutput, sceneSchema} from "@/scenes/SceneSchema.ts";
import ImageUploadField from "@/images/ImageUploadField.tsx";
import {useTemporaryImageFormField} from "@/images/useTemporaryImageFormField.ts";
import {notifyError} from "@/api/ErrorNotification.ts";

export default function SceneForm({scene, campaignId, campaignTitle, onSubmit, onSaved, onCancel, onDirtyChange}: {
    scene?: Scene;
    campaignId: string;
    campaignTitle: string;
    onSubmit: (scene: Scene) => Promise<Scene>;
    onSaved: (scene: Scene) => void;
    onCancel: () => void;
    onDirtyChange: (dirty: boolean) => void;
}) {
    const [saveState, setSaveState] = useState<SaveState>("idle");
    const [error, setError] = useState<string>();
    const form = useForm<SceneFormInput, unknown, SceneFormOutput>({
        resolver: zodResolver(sceneSchema),
        mode: "onBlur",
        defaultValues: scene ?? {id: UUID(), name: "", campaignId},
    });
    const imageField = useTemporaryImageFormField({
        form,
        name: "image",
        onUploadStart: () => setError(undefined),
    });

    useEffect(() => onDirtyChange(form.formState.isDirty), [form.formState.isDirty, onDirtyChange]);
    const onValid = async (data: SceneFormOutput) => {
        setSaveState("saving");
        setError(undefined);
        try {
            const saved = await onSubmit(data);
            imageField.commit();
            form.reset(saved);
            setSaveState("success");
            onSaved(saved);
        } catch (reason) {
            setSaveState("error");
            notifyError(reason, "Die Szene konnte nicht gespeichert werden.");
        }
    };

    const onInvalid = (errors: FieldErrors<SceneFormOutput>) => {
        console.error("Form errors", errors);
        setSaveState("error");
        setError("Bitte überprüfe die Eingaben.");
    };

    const handleSubmit = form.handleSubmit(onValid, onInvalid);

    return (
        <Form {...form}>
            <form onSubmit={handleSubmit} className="flex h-full min-h-0 flex-col gap-2">
                <HiddenField name="id"/>
                <FormToolbar title={scene ? "Szene bearbeiten" : "Neue Szene"}
                             subtitle={campaignTitle}
                             saveState={saveState}
                             saveDisabled={!form.formState.isDirty || imageField.uploading || saveState === "saving"}
                             onCancel={onCancel}/>
                {error && <p role="alert" className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">{error}</p>}
                <section className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg border bg-card">
                    <div className="shrink-0 border-b p-3">
                        <TextInputField name="name" title="Name" placeholder="Szenenname"/>
                    </div>
                    <ImageUploadField image={imageField.image}
                                      uploading={imageField.uploading}
                                      onFileSelected={imageField.onFileSelected}
                                      alt="Szenenbild"
                                      className="flex-1"/>
                </section>
            </form>
        </Form>
    );
}
